import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { SECTORS } from "../../lib/sectors";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ENGAGEMENT_TYPES = [
  "Strategic Advisory",
  "Governance Modernization",
  "Transformation PMO",
  "Policy Design",
  "Public-Private Partnership",
  "Other",
];

const initialForm = {
  name: "",
  email: "",
  organization: "",
  designation: "",
  sector: SECTORS[0].short,
  engagement_type: ENGAGEMENT_TYPES[0],
  message: "",
};

export const ContactForm = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSubmitted(true);
      toast.success("Inquiry received", {
        description:
          "Our advisory desk will revert within two business days.",
      });
      setForm(initialForm);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error("Could not submit inquiry", {
        description:
          typeof detail === "string"
            ? detail
            : "Please review the form and try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="engage"
      data-testid="engage-section"
      className="relative bg-navy text-ivory py-28 md:py-40 scroll-mt-nav overflow-hidden grain"
    >
      <div
        className="absolute inset-0 bg-grid-faint opacity-20"
        style={{ backgroundSize: "80px 80px" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-navy-900 to-navy" />

      <div className="relative container-bharat">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <p className="overline text-gold">Engagement</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.02] tracking-tight">
              Partnering to build the{" "}
              <span className="italic text-gold">Bharat of tomorrow</span>.
            </h2>
            <p className="mt-8 text-ivory/70 max-w-md leading-relaxed">
              Engage our advisory board on a strategic mandate, transformation
              program or institutional partnership. Inquiries are reviewed by a
              partner within two business days.
            </p>

            <div className="hairline my-12 max-w-sm" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <p className="overline text-gold/80 mb-3">Pune · Headquarters</p>
                <p className="text-sm text-ivory/75 leading-relaxed">
                  Senapati Bapat Road, Shivajinagar
                  <br />
                  Pune 411016, Maharashtra
                </p>
              </div>
              <div>
                <p className="overline text-gold/80 mb-3">Direct</p>
                <p className="text-sm text-ivory/75 leading-relaxed">
                  advisory@axisbharat.com
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="inquiry-success"
                className="border border-gold/40 bg-navy-700/40 p-10 lg:p-14"
              >
                <CheckCircle2 className="text-gold" size={36} />
                <h3 className="font-serif-display text-3xl mt-6">
                  Your inquiry has reached our advisory desk.
                </h3>
                <p className="mt-4 text-ivory/70 max-w-md leading-relaxed">
                  A partner will personally review your engagement and revert
                  within two business days.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  data-testid="inquiry-new"
                  className="mt-8 inline-flex items-center gap-2 text-sm text-gold underline-grow"
                >
                  Submit another inquiry
                </button>
              </motion.div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="inquiry-form"
                className="border border-ivory/15 bg-navy-700/30 p-8 lg:p-12 backdrop-blur-sm"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <Field
                    label="Name"
                    testid="field-name"
                    value={form.name}
                    onChange={update("name")}
                    required
                  />
                  <Field
                    label="Work Email"
                    testid="field-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    required
                  />
                  <Field
                    label="Organization"
                    testid="field-organization"
                    value={form.organization}
                    onChange={update("organization")}
                    required
                  />
                  <Field
                    label="Designation"
                    testid="field-designation"
                    value={form.designation}
                    onChange={update("designation")}
                  />
                  <Select
                    label="Sector of Interest"
                    testid="field-sector"
                    value={form.sector}
                    onChange={update("sector")}
                    options={SECTORS.map((s) => s.short)}
                  />
                  <Select
                    label="Engagement Type"
                    testid="field-engagement"
                    value={form.engagement_type}
                    onChange={update("engagement_type")}
                    options={ENGAGEMENT_TYPES}
                  />
                </div>

                <div className="mt-8">
                  <label className="overline text-gold/80 mb-3 block">
                    Brief
                  </label>
                  <textarea
                    data-testid="field-message"
                    required
                    minLength={10}
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Outline the strategic question, scope and any timelines."
                    className="w-full bg-transparent border-b border-ivory/25 focus:border-gold outline-none py-3 text-ivory placeholder:text-ivory/30 text-sm leading-relaxed transition-colors"
                  />
                </div>

                <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                  <p className="text-xs text-ivory/45 max-w-sm">
                    By submitting, you agree to be contacted by Bharat Axis
                    Advisory regarding your engagement.
                  </p>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="inquiry-submit"
                    className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-gold text-navy text-sm tracking-wide hover:bg-ivory transition-all duration-300 disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Submitting…
                      </>
                    ) : (
                      <>
                        Submit Inquiry
                        <Send size={14} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, testid, ...props }) => (
  <label className="block">
    <span className="overline text-gold/80 mb-2 block">{label}</span>
    <input
      data-testid={testid}
      {...props}
      className="w-full bg-transparent border-b border-ivory/25 focus:border-gold outline-none py-2.5 text-ivory placeholder:text-ivory/30 text-sm transition-colors"
    />
  </label>
);

const Select = ({ label, testid, options, ...props }) => (
  <label className="block">
    <span className="overline text-gold/80 mb-2 block">{label}</span>
    <select
      data-testid={testid}
      {...props}
      className="w-full bg-transparent border-b border-ivory/25 focus:border-gold outline-none py-2.5 text-ivory text-sm transition-colors appearance-none"
      style={{
        backgroundImage:
          "linear-gradient(45deg, transparent 50%, #C5A880 50%), linear-gradient(135deg, #C5A880 50%, transparent 50%)",
        backgroundPosition: "calc(100% - 14px) 50%, calc(100% - 9px) 50%",
        backgroundSize: "5px 5px, 5px 5px",
        backgroundRepeat: "no-repeat",
      }}
    >
      {options.map((o) => (
        <option key={o} value={o} className="bg-navy text-ivory">
          {o}
        </option>
      ))}
    </select>
  </label>
);

export default ContactForm;
